import { Spin } from 'antd';
import { Navigate, createBrowserRouter, useParams } from 'react-router-dom';
import { isLeaderExperience } from '@/constants/capabilities';
import { USER_ROLES } from '@/constants/roles';
import { ROUTES, getHomePath } from '@/constants/routes';
import { useAuthProfileReady } from '@/hooks/useAuthProfileReady';
import { AdminLayout } from '@/layouts/AdminLayout';
import { EmployeeMobileLayout } from '@/layouts/mobile/EmployeeMobileLayout';
import { LeaderMobileLayout } from '@/layouts/mobile/LeaderMobileLayout';
import { DepartmentManagementPage } from '@/pages/admin/departments/DepartmentManagementPage';
import { ReportManagementPage } from '@/pages/admin/reports/ReportManagementPage';
import { ProjectCreatePage } from '@/pages/admin/projects/ProjectCreatePage';
import { ProjectDetailPage } from '@/pages/admin/projects/ProjectDetailPage';
import { ProjectListPage } from '@/pages/admin/projects/ProjectListPage';
import { ProjectReportManagementPage } from '@/pages/admin/project-reports/ProjectReportManagementPage';
import { UserManagementPage } from '@/pages/admin/users/UserManagementPage';
import { PlaceholderPage } from '@/pages/common/PlaceholderPage';
import { LoginPage } from '@/pages/login/LoginPage';
import { EmployeeHomePage } from '@/pages/mobile/employee/EmployeeHomePage';
import { EmployeeLeaderPage } from '@/pages/mobile/employee/EmployeeLeaderPage';
import { EmployeeMinePage } from '@/pages/mobile/employee/EmployeeMinePage';
import { EmployeeNotificationDetailMobilePage } from '@/pages/mobile/employee/EmployeeNotificationDetailMobilePage';
import { EmployeeNotificationsPage } from '@/pages/mobile/employee/EmployeeNotificationsPage';
import { EmployeeProjectsPage } from '@/pages/mobile/employee/EmployeeProjectsPage';
import { EmployeeProjectDetailMobilePage } from '@/pages/mobile/employee/EmployeeProjectDetailMobilePage';
import { EmployeeProfilePage } from '@/pages/mobile/employee/EmployeeProfilePage';
import { EmployeeReportsPage } from '@/pages/mobile/employee/EmployeeReportsPage';
import { EmployeeTaskDetailMobilePage } from '@/pages/mobile/employee/EmployeeTaskDetailMobilePage';
import { EmployeeTasksPage } from '@/pages/mobile/employee/EmployeeTasksPage';
import { ReportHistoryMobilePage } from '@/pages/mobile/employee/ReportHistoryMobilePage';
import { ChangePasswordPage } from '@/pages/mobile/leader/ChangePasswordPage';
import { LeaderHomePage } from '@/pages/mobile/leader/LeaderHomePage';
import { LeaderMinePage } from '@/pages/mobile/leader/LeaderMinePage';
import { LeaderNotificationDetailMobilePage } from '@/pages/mobile/leader/LeaderNotificationDetailMobilePage';
import { LeaderNotificationsPage } from '@/pages/mobile/leader/LeaderNotificationsPage';
import { LeaderProjectDetailMobilePage } from '@/pages/mobile/leader/LeaderProjectDetailMobilePage';
import { LeaderProjectsPage } from '@/pages/mobile/leader/LeaderProjectsPage';
import { LeaderProfilePage } from '@/pages/mobile/leader/LeaderProfilePage';
import { LeaderReportMobilePage } from '@/pages/mobile/leader/LeaderReportMobilePage';
import { LeaderTaskDetailMobilePage } from '@/pages/mobile/leader/LeaderTaskDetailMobilePage';
import { TeamReportDetailMobilePage } from '@/pages/mobile/leader/TeamReportDetailMobilePage';
import { LeadershipReportsMobilePage } from '@/pages/mobile/leader/LeadershipReportsMobilePage';
import { TeamReportsMobilePage } from '@/pages/mobile/leader/TeamReportsMobilePage';
import { LeaderProjectReportDetailRoute } from '@/pages/mobile/shared/LeaderProjectReportDetailRoute';
import { ProjectReportDetailMobilePage } from '@/pages/mobile/shared/ProjectReportDetailMobilePage';
import { ProjectReportSubmitRedirectPage } from '@/pages/mobile/shared/ProjectReportSubmitRedirectPage';
import { AuthGuard } from '@/router/guards/AuthGuard';
import { BusinessUserGuard } from '@/router/guards/BusinessUserGuard';
import { GuestGuard } from '@/router/guards/GuestGuard';
import { MobileEmployeeGuard } from '@/router/guards/MobileEmployeeGuard';
import { MobileLeaderGuard } from '@/router/guards/MobileLeaderGuard';
import { RoleGuard } from '@/router/guards/RoleGuard';
import { selectIsAuthenticated, useAuthStore } from '@/stores/auth.store';

function RootRedirect() {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore((state) => state.user);
  const { ready } = useAuthProfileReady();

  if (!isAuthenticated || !user) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (!ready) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
        <Spin />
      </div>
    );
  }

  return <Navigate to={getHomePath(user)} replace />;
}

function LegacyMobileRedirect() {
  const { segment } = useParams<{ segment: string }>();
  const user = useAuthStore((state) => state.user);
  const { ready } = useAuthProfileReady();

  if (!user) {
    return <Navigate to={ROUTES.login} replace />;
  }

  if (!ready) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '40vh' }}>
        <Spin />
      </div>
    );
  }

  if (isLeaderExperience(user)) {
    const leaderMap: Record<string, string> = {
      report: ROUTES.mobile.leader.myReport,
      history: ROUTES.mobile.leader.history,
      team: ROUTES.mobile.leader.team,
      reports: ROUTES.mobile.leader.team,
      leadership: ROUTES.mobile.leader.leadership,
      'leadership-reports': ROUTES.mobile.leader.leadership,
      home: ROUTES.mobile.leader.home,
      mine: ROUTES.mobile.leader.mine,
    };
    return <Navigate to={(segment && leaderMap[segment]) || ROUTES.mobile.leader.home} replace />;
  }

  const employeeMap: Record<string, string> = {
    report: ROUTES.mobile.employee.reports,
    reports: ROUTES.mobile.employee.reports,
    history: `${ROUTES.mobile.employee.reports}?tab=history`,
    home: ROUTES.mobile.employee.home,
    mine: ROUTES.mobile.employee.mine,
  };
  return <Navigate to={(segment && employeeMap[segment]) || ROUTES.mobile.employee.report} replace />;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: ROUTES.login,
    element: (
      <GuestGuard>
        <LoginPage />
      </GuestGuard>
    ),
  },
  {
    path: ROUTES.admin.root,
    element: (
      <AuthGuard>
        <RoleGuard allowedRoles={[USER_ROLES.ADMIN]}>
          <AdminLayout />
        </RoleGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.admin.dashboard} replace />,
      },
      {
        path: 'dashboard',
        element: <PlaceholderPage title="管理员工作台" description="业务页面开发中" />,
      },
      {
        path: 'departments',
        element: <DepartmentManagementPage />,
      },
      {
        path: 'users',
        element: <UserManagementPage />,
      },
      {
        path: 'reports',
        element: <ReportManagementPage />,
      },
      {
        path: 'project-reports',
        element: <ProjectReportManagementPage />,
      },
      {
        path: 'projects',
        element: <ProjectListPage />,
      },
      {
        path: 'projects/create',
        element: <ProjectCreatePage />,
      },
      {
        path: 'projects/:id',
        element: <ProjectDetailPage />,
      },
    ],
  },
  {
    path: ROUTES.mobile.leader.root,
    element: (
      <AuthGuard>
        <BusinessUserGuard>
          <MobileLeaderGuard>
            <LeaderMobileLayout />
          </MobileLeaderGuard>
        </BusinessUserGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.mobile.leader.home} replace />,
      },
      {
        path: 'home',
        element: <LeaderHomePage />,
      },
      {
        path: 'team',
        element: <TeamReportsMobilePage />,
      },
      {
        path: 'leadership',
        element: <LeadershipReportsMobilePage />,
      },
      {
        path: 'projects/:projectId/reports/submit',
        element: <ProjectReportSubmitRedirectPage variant="leader" />,
      },
      {
        path: 'projects/:projectId/reports/:reportId',
        element: <LeaderProjectReportDetailRoute />,
      },
      {
        path: 'projects/:projectId/tasks/:taskId',
        element: <LeaderTaskDetailMobilePage />,
      },
      {
        path: 'projects/:projectId',
        element: <LeaderProjectDetailMobilePage />,
      },
      {
        path: 'projects',
        element: <LeaderProjectsPage />,
      },
      {
        path: 'notifications/:receiverId',
        element: <LeaderNotificationDetailMobilePage />,
      },
      {
        path: 'notifications',
        element: <LeaderNotificationsPage />,
      },
      {
        path: 'team/:reportId',
        element: <TeamReportDetailMobilePage />,
      },
      {
        path: 'mine',
        element: <LeaderMinePage />,
      },
      {
        path: 'my-report',
        element: <LeaderReportMobilePage />,
      },
      {
        path: 'report',
        element: <Navigate to={ROUTES.mobile.leader.myReport} replace />,
      },
      {
        path: 'history',
        element: <ReportHistoryMobilePage />,
      },
      {
        path: 'profile',
        element: <LeaderProfilePage />,
      },
      {
        path: 'password',
        element: <ChangePasswordPage />,
      },
    ],
  },
  {
    path: ROUTES.mobile.employee.root,
    element: (
      <AuthGuard>
        <BusinessUserGuard>
          <MobileEmployeeGuard>
            <EmployeeMobileLayout />
          </MobileEmployeeGuard>
        </BusinessUserGuard>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.mobile.employee.home} replace />,
      },
      {
        path: 'home',
        element: <EmployeeHomePage />,
      },
      {
        path: 'reports',
        element: <EmployeeReportsPage />,
      },
      {
        path: 'tasks/:taskId',
        element: <EmployeeTaskDetailMobilePage />,
      },
      {
        path: 'projects/:projectId/reports/submit',
        element: <ProjectReportSubmitRedirectPage variant="employee" />,
      },
      {
        path: 'projects/:projectId/reports/:reportId',
        element: <ProjectReportDetailMobilePage variant="employee" />,
      },
      {
        path: 'projects/:projectId',
        element: <EmployeeProjectDetailMobilePage />,
      },
      {
        path: 'projects',
        element: <EmployeeProjectsPage />,
      },
      {
        path: 'tasks',
        element: <EmployeeTasksPage />,
      },
      {
        path: 'notifications/:receiverId',
        element: <EmployeeNotificationDetailMobilePage />,
      },
      {
        path: 'notifications',
        element: <EmployeeNotificationsPage />,
      },
      {
        path: 'mine',
        element: <EmployeeMinePage />,
      },
      {
        path: 'profile',
        element: <EmployeeProfilePage />,
      },
      {
        path: 'leader',
        element: <EmployeeLeaderPage />,
      },
      {
        path: 'report',
        element: <Navigate to={ROUTES.mobile.employee.reports} replace />,
      },
      {
        path: 'history',
        element: <Navigate to={`${ROUTES.mobile.employee.reports}?tab=history`} replace />,
      },
    ],
  },
  {
    path: ROUTES.work.root,
    element: <LegacyMobileRedirect />,
  },
  {
    path: `${ROUTES.work.root}/:segment`,
    element: <LegacyMobileRedirect />,
  },
  {
    path: ROUTES.leader.root,
    element: <LegacyMobileRedirect />,
  },
  {
    path: `${ROUTES.leader.root}/report/:reportId`,
    element: <LeaderLegacyReportDetailRedirect />,
  },
  {
    path: `${ROUTES.leader.root}/:segment`,
    element: <LegacyMobileRedirect />,
  },
  {
    path: ROUTES.employee.root,
    element: <LegacyMobileRedirect />,
  },
  {
    path: `${ROUTES.employee.root}/:segment`,
    element: <LegacyMobileRedirect />,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);

function LeaderLegacyReportDetailRedirect() {
  const { reportId } = useParams<{ reportId: string }>();
  if (!reportId) {
    return <Navigate to={ROUTES.mobile.leader.team} replace />;
  }
  return <Navigate to={ROUTES.mobile.leader.teamReportDetail(reportId)} replace />;
}
